import { useEffect, useState, useCallback, useRef } from "react";
import SearchInput from "./SearchInput";

interface Props {
  defaultData: any[] | undefined;
  searchByProperty?: string;
  filterFn?: (item: any, query: string) => boolean;
  setData: (data: any[]) => void;
  debounceTime?: number;
  resetSearch?: number;
  setResetSearch?: (resetSearch: number) => void;
  onSearchChange?: (query: string) => void;
}

const LocalSearch = ({
  defaultData,
  searchByProperty,
  filterFn,
  setData,
  debounceTime = 300,
  resetSearch,
  setResetSearch,
  onSearchChange,
}: Props) => {
  const [search, setSearch] = useState("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const defaultDataRef = useRef(defaultData);

  useEffect(() => {
    defaultDataRef.current = defaultData;
  }, [defaultData]);

  const handleSearch = useCallback(
    (v: string) => {
      setSearch(v);
      onSearchChange?.(v);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        const currentDefaultData = defaultDataRef.current;
        if (!currentDefaultData) return;
        if (v.trim().length > 0) {
          const query = v.toLowerCase();
          const filteredData = currentDefaultData.filter((item: any) => {
            if (filterFn) return filterFn(item, query);
            return item[searchByProperty!]
              ?.toString()
              .toLowerCase()
              .includes(query);
          });
          setData(filteredData);
        } else {
          setData(currentDefaultData);
        }
      }, debounceTime);
    },
    [searchByProperty, filterFn, setData, debounceTime, onSearchChange],
  );

  useEffect(() => {
    if (resetSearch) {
      setSearch("");
      onSearchChange?.("");
      setData(defaultDataRef.current ?? []);
      setResetSearch?.(0);
    }
  }, [resetSearch, setResetSearch, setData, onSearchChange]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return <SearchInput value={search} onChange={handleSearch} />;
};

export default LocalSearch;
